import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supportChatApi } from '../../services/supportChatService';
import ChatWidgetShell from './ChatWidgetShell';
import {
  createChatMessage,
  buildChatHistory,
  loadStoredChat,
  persistChat,
  formatChatDisplayText,
} from '../../utils/chatWidgetUtils';

const STORAGE_PREFIX = 'support-chat-history';

const welcomeForRole = (role, name, enabled) => {
  const greeting = name ? `Hi ${name.split(' ')[0]}!` : 'Hi there!';
  if (!enabled) {
    return `${greeting} Support chat is temporarily unavailable. Please use the Help Center or contact support by email.`;
  }
  if (role === 'vendor') {
    return `${greeting} I'm your vendor assistant. Ask me anything—e.g. **How do I upgrade my plan?**, payouts, bookings, invoices, or services.`;
  }
  return `${greeting} I'm your support assistant. I can help with bookings, payments, your account, and using the platform.`;
};

const VENDOR_QUICK_PROMPTS = [
  'How do I upgrade my plan?',
  'Where are my payouts?',
  'How do I create an invoice?',
];

const CLIENT_QUICK_PROMPTS = [
  'How do I book a vendor?',
  'How do I pay my balance?',
  'Where are my bookings?',
];

const ChatWidget = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const role = user?.user_type;
  const isSupportedUser = isAuthenticated && (role === 'client' || role === 'vendor');

  const storageKey = user?.id ? `${STORAGE_PREFIX}:${user.id}` : null;

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [assistantLabel, setAssistantLabel] = useState('AI Assistant');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);

  const abortRef = useRef(null);

  useEffect(() => {
    if (!isSupportedUser || !storageKey) return;

    let cancelled = false;

    const init = async () => {
      let enabled = true;
      let label = role === 'vendor' ? 'Vendor Assistant' : 'Support Assistant';
      try {
        const res = await supportChatApi.getStatus();
        enabled = Boolean(res?.data?.enabled);
        label = res?.data?.assistant_name || label;
      } catch {
        enabled = false;
      }
      if (cancelled) return;
      setChatEnabled(enabled);
      setAssistantLabel(label);
      setMessages(loadStoredChat(storageKey, welcomeForRole(role, user?.name, enabled)));
      setStatusChecked(true);
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [isSupportedUser, storageKey, role, user?.name]);

  useEffect(() => {
    persistChat(storageKey, messages);
  }, [messages, storageKey]);

  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener('openChatWidget', handleOpenChat);
    return () => window.removeEventListener('openChatWidget', handleOpenChat);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const sendToAssistant = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      if (!chatEnabled) {
        setError('AI chat is not configured. Add GEMINI_API_KEY to the API .env file.');
        return;
      }

      setError(null);
      setMessages((prev) => [...prev, createChatMessage(trimmed, 'user')]);
      setInputValue('');
      setIsSending(true);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await supportChatApi.sendMessage({
          message: trimmed,
          history: buildChatHistory(messages),
          signal: controller.signal,
        });

      const reply = formatChatDisplayText(res?.data?.reply);
      if (!reply) throw new Error('Empty response from assistant.');

      setMessages((prev) => [...prev, createChatMessage(reply, 'bot')]);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(err?.response?.data?.message || err?.message || 'Something went wrong.');
        setMessages((prev) => [
          ...prev,
          createChatMessage(
            "I couldn't complete that request. Try again or visit the Help Center.",
            'bot',
            { isError: true }
          ),
        ]);
      } finally {
        setIsSending(false);
        abortRef.current = null;
      }
    },
    [chatEnabled, isSending, messages]
  );

  const handleClear = () => {
    abortRef.current?.abort();
    setIsSending(false);
    setError(null);
    setMessages([createChatMessage(welcomeForRole(role, user?.name, chatEnabled), 'bot', { isWelcome: true })]);
    if (storageKey) sessionStorage.removeItem(storageKey);
  };

  if (authLoading || !isSupportedUser || !statusChecked) {
    return null;
  }

  const quickPrompts = role === 'vendor' ? VENDOR_QUICK_PROMPTS : CLIENT_QUICK_PROMPTS;

  return (
    <ChatWidgetShell
      variant="support"
      isOpen={isOpen}
      isMinimized={isMinimized}
      chatEnabled={chatEnabled}
      assistantLabel={assistantLabel}
      subtitle={chatEnabled ? 'Powered by Gemini' : 'Offline'}
      messages={messages}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSend={sendToAssistant}
      isSending={isSending}
      error={error}
      onToggle={() => {
        if (isOpen && !isMinimized) setIsMinimized(true);
        else if (isOpen && isMinimized) setIsMinimized(false);
        else {
          setIsOpen(true);
          setIsMinimized(false);
        }
      }}
      onClose={() => {
        abortRef.current?.abort();
        setIsOpen(false);
        setIsMinimized(false);
      }}
      onClear={handleClear}
      quickPrompts={quickPrompts}
      onQuickPrompt={sendToAssistant}
    />
  );
};

export default ChatWidget;
